import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { revalidateLogic } from '@tanstack/react-form';

import { mutateAudienceCreate } from '@/api/audience';
import { useIntegrationCRMProviders } from '@/api/integration';
import PlugConnect from '@/assets/svg/bx-plug-connect.svg?react';
import { Button, LinkButton } from '@/components/buttons';
import { useAppForm } from '@/components/Form';
import { PageSpinner } from '@/components/loaders';
import { Page } from '@/components/page';
import { PendoPlaceholder } from '@/components/PendoPlaceholder';
import Prompt from '@/components/Prompt';
import { Results } from '@/components/Results';
import { ReturnToLink } from '@/components/ReturnToLink';
import { exampleAIQuestions } from '@/constants/exampleAIQuestions';
import { toTanStackFormFieldErrors } from '@/error/tanstack-form-field-errors';
import ErrorOrChildren from '@/features/ErrorOrChildren';
import useInstanceNavigate from '@/hooks/useInstanceNavigate';
import { useNotification } from '@/providers/Notification';
import { ROUTES } from '@/router';

import {
  AI_AUDIENCE_PROFILE,
  createAIAudienceDefaultValues,
  type CreateAIAudienceFormValues,
} from './CreateAIAudienceContent';
import CreateAIAudienceContent from './CreateAIAudienceContent';

const CreateAIAudience = () => {
  const navigate = useInstanceNavigate();
  const { pushNotification } = useNotification();
  const { providers, hasHubspotConnection, hasSalesforceConnection, isLoading, error } =
    useIntegrationCRMProviders();
  const hasNoCRMConnection = !hasHubspotConnection && !hasSalesforceConnection;

  const form = useAppForm({
    defaultValues: createAIAudienceDefaultValues,
    validationLogic: revalidateLogic(),
    validators: {
      onSubmitAsync: async ({ value }: { value: CreateAIAudienceFormValues }) => {
        const shouldSelectAudience = value.profile === AI_AUDIENCE_PROFILE.CUSTOM;

        try {
          await mutateAudienceCreate({
            type: 'lookalike',
            name: value.name,
            description: value.description,
            lookalikeConfig: {
              audienceId: shouldSelectAudience ? (value.audience?.id ?? '') : '',
              includeSampleCompanies: value.includeSampleCompanies,
              profile: value.profile,
            },
          });

          navigate(ROUTES.audiences.path, {
            state: {
              successMessage: t`Audience was successfully created.`,
            },
          });
        } catch (error) {
          const fieldErrors = toTanStackFormFieldErrors(error);
          if (fieldErrors) {
            return fieldErrors;
          }

          pushNotification({
            type: 'error',
            message: t`An unknown error has occurred, please try again or contact support.`,
          });
        }
      },
    },
  });

  return (
    <Page title={t`New Audience`} exampleAIQuestions={exampleAIQuestions('audience')}>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <form.AppForm>
          <form.Subscribe
            selector={(state) => ({
              isDirty: state.isDirty,
              isSubmitting: state.isSubmitting,
            })}
          >
            {({ isDirty, isSubmitting }) => (
              <Prompt
                message={t`Are you sure you want to exit without saving your new Audience?`}
                shouldPreventNavigation={isDirty && !isSubmitting}
              />
            )}
          </form.Subscribe>

          <div className="flex flex-col gap-6">
            <div className="flex items-center justify-between gap-8 max-md:flex-col max-md:items-start max-md:gap-4">
              <ReturnToLink route={ROUTES.audiences.path} routeName={t`Audiences`} />
              <div className="flex items-center gap-4 max-md:w-full max-md:justify-end">
                <PendoPlaceholder />
                <LinkButton to={ROUTES.audiences.path}>
                  <Trans>Cancel</Trans>
                </LinkButton>
                {hasNoCRMConnection ? (
                  <Button variant="primary" disabled>
                    <Trans>Save</Trans>
                  </Button>
                ) : (
                  <form.SubmitButton variant="primary">
                    <Trans>Save</Trans>
                  </form.SubmitButton>
                )}
              </div>
            </div>

            <ErrorOrChildren
              error={error}
              message404={<Trans>The CRM you're looking for does not exist</Trans>}
            >
              {!providers && isLoading && <PageSpinner />}

              {!isLoading && hasNoCRMConnection && (
                <Results
                  icon={<PlugConnect />}
                  title={<Trans>Connect your CRM</Trans>}
                  subtitle={<Trans>Please connect your CRM to create AI audiences.</Trans>}
                  extra={
                    <LinkButton variant="primary" to={ROUTES.integrationByCategory('crm')}>
                      <Trans>Connect CRM</Trans>
                    </LinkButton>
                  }
                  isFullPage
                />
              )}

              {!hasNoCRMConnection && <CreateAIAudienceContent />}
            </ErrorOrChildren>
          </div>
        </form.AppForm>
      </form>
    </Page>
  );
};

export default CreateAIAudience;
